"use client";

import { c as _c } from "react/compiler-runtime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faInfoCircle, faTriangleExclamation, faXmark } from "@fortawesome/free-solid-svg-icons";
import CommonCalendar from "@/components/utils/calendar/CommonCalendar";
import type { CreateWaveDatesConfig } from "@/types/waves.types";
import DateAccordion from "@/components/common/DateAccordion";
import TimePicker from "@/components/common/TimePicker";
import TooltipIconButton from "@/components/common/TooltipIconButton";
import { countTotalDecisions, formatDate, getMinimumRollingEndDate } from "../services/waveDecisionService";
import { calculateLastDecisionTime } from "@/helpers/waves/create-wave.helpers";
import { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
interface RollingEndDateProps {
  readonly dates: CreateWaveDatesConfig;
  readonly errors: CREATE_WAVE_VALIDATION_ERROR[];
  readonly setDates: (dates: CreateWaveDatesConfig) => void;
  readonly isExpanded: boolean;
  readonly setIsExpanded: (expanded: boolean) => void;
}
interface RollingEndDateCollapsedContentProps {
  readonly endDate: number | null;
}
function RollingEndDateCollapsedContent(t0) {
  const $ = _c(6);
  const {
    endDate
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FontAwesomeIcon icon={faCalendarAlt} className="tw-mr-2 tw-size-4 tw-text-primary-400" />;
    t2 = <p className="tw-mb-0 tw-text-xs tw-text-iron-300/70">Wave End Date</p>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== endDate) {
    t3 = endDate === null ? "No end date" : formatDate(endDate);
    $[2] = endDate;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <div className="tw-flex tw-items-center tw-rounded-lg tw-bg-iron-700/40 tw-px-3 tw-py-2 tw-shadow-md tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]">{t1}<div>{t2}<p className="tw-mb-0 tw-text-sm tw-font-medium tw-text-iron-50">{t3}</p></div></div>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
export default function RollingEndDate(t0) {
  const $ = _c(135);
  const {
    dates,
    errors,
    setDates,
    isExpanded,
    setIsExpanded
  } = t0;
  const isRollingMode = dates.isRolling;
  let t1;
  if ($[0] !== errors) {
    t1 = errors.includes(CREATE_WAVE_VALIDATION_ERROR.RANK_DECISION_TIME_MUST_BE_IN_FUTURE);
    $[0] = errors;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasRankFutureDateError = t1;
  let t2;
  if ($[2] !== errors) {
    t2 = errors.includes(CREATE_WAVE_VALIDATION_ERROR.END_DATE_MUST_BE_AFTER_VOTING_START_DATE);
    $[2] = errors;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const hasEndDateBeforeVotingStartError = t2;
  const shouldShowExpandedContent = isExpanded || hasRankFutureDateError || hasEndDateBeforeVotingStartError;
  const selectedEndDate = dates.endDate !== null && Number.isFinite(dates.endDate) ? dates.endDate : null;
  const hasSelectedEndDate = selectedEndDate !== null;
  let T0;
  let T1;
  let calculateTotalDecisions;
  let displayedHours;
  let displayedMinutes;
  let handleDateSelection;
  let handleTimeChange;
  let minEndDate;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== dates || $[5] !== hasEndDateBeforeVotingStartError || $[6] !== hasRankFutureDateError || $[7] !== hasSelectedEndDate || $[8] !== selectedEndDate || $[9] !== setDates || $[10] !== setIsExpanded || $[11] !== shouldShowExpandedContent) {
    minEndDate = getMinimumRollingEndDate(dates.firstDecisionTime, dates.subsequentDecisions);
    const displayedTimestamp = selectedEndDate ?? minEndDate;
    const displayedDate = new Date(displayedTimestamp);
    displayedHours = displayedDate.getHours();
    displayedMinutes = displayedDate.getMinutes();
    handleDateSelection = timestamp => {
      const date = new Date(timestamp);
      date.setHours(displayedHours, displayedMinutes, 0, 0);
      setDates({
        ...dates,
        endDate: date.getTime()
      });
    };
    let t19;
    if ($[36] !== dates || $[37] !== selectedEndDate || $[38] !== setDates) {
      t19 = (h, m) => {
        if (selectedEndDate === null) {
          return;
        }
        const date_0 = new Date(selectedEndDate);
        date_0.setHours(h, m, 0, 0);
        setDates({
          ...dates,
          endDate: date_0.getTime()
        });
      };
      $[36] = dates;
      $[37] = selectedEndDate;
      $[38] = setDates;
      $[39] = t19;
    } else {
      t19 = $[39];
    }
    handleTimeChange = t19;
    let t20;
    if ($[40] !== dates || $[41] !== setDates) {
      t20 = () => {
        setDates({
          ...dates,
          endDate: null
        });
      };
      $[40] = dates;
      $[41] = setDates;
      $[42] = t20;
    } else {
      t20 = $[42];
    }
    const handleClearEndDate = t20;
    let t21;
    if ($[43] !== dates.firstDecisionTime || $[44] !== dates.subsequentDecisions || $[45] !== selectedEndDate) {
      t21 = () => {
        if (selectedEndDate === null) {
          return 0;
        }
        return countTotalDecisions(dates.firstDecisionTime, dates.subsequentDecisions, selectedEndDate);
      };
      $[43] = dates.firstDecisionTime;
      $[44] = dates.subsequentDecisions;
      $[45] = selectedEndDate;
      $[46] = t21;
    } else {
      t21 = $[46];
    }
    calculateTotalDecisions = t21;
    let t22;
    if ($[47] !== selectedEndDate) {
      t22 = <RollingEndDateCollapsedContent endDate={selectedEndDate} />;
      $[47] = selectedEndDate;
      $[48] = t22;
    } else {
      t22 = $[48];
    }
    const collapsedContent = t22;
    t18 = "tw-relative";
    T1 = DateAccordion;
    t13 = "Optional Wave End Date";
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <TooltipIconButton icon={faInfoCircle} tooltipText="Set when your wave will officially end. Leave it blank for no end date." tooltipPosition="bottom" tooltipWidth="tw-w-80" />;
      $[49] = t14;
    } else {
      t14 = $[49];
    }
    t15 = shouldShowExpandedContent;
    if ($[50] !== setIsExpanded || $[51] !== shouldShowExpandedContent) {
      t16 = () => setIsExpanded(!shouldShowExpandedContent);
      $[50] = setIsExpanded;
      $[51] = shouldShowExpandedContent;
      $[52] = t16;
    } else {
      t16 = $[52];
    }
    t17 = collapsedContent;
    t12 = "tw-px-5 tw-pb-5 tw-pt-2";
    t8 = "tw-col-span-2";
    let t23;
    if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <div><p className="tw-mb-1 tw-text-base tw-font-medium tw-text-iron-50">Set Optional End Date</p><p className="tw-mb-0 tw-text-xs tw-text-iron-400">Leave blank for no end date.</p></div>;
      $[53] = t23;
    } else {
      t23 = $[53];
    }
    let t24;
    if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = <p className="tw-mb-0 tw-text-xs tw-text-iron-300/70">Wave Ends</p>;
      $[54] = t24;
    } else {
      t24 = $[54];
    }
    let t25;
    if ($[55] !== selectedEndDate) {
      t25 = selectedEndDate === null ? "No end date" : formatDate(selectedEndDate);
      $[55] = selectedEndDate;
      $[56] = t25;
    } else {
      t25 = $[56];
    }
    let t26;
    if ($[57] !== t25) {
      t26 = <p className="tw-mb-0 tw-text-sm tw-font-medium tw-text-iron-50">{t25}</p>;
      $[57] = t25;
      $[58] = t26;
    } else {
      t26 = $[58];
    }
    let t27;
    if ($[59] !== handleClearEndDate || $[60] !== hasSelectedEndDate) {
      t27 = hasSelectedEndDate && <button type="button" aria-label="Clear end date" onClick={handleClearEndDate} className="tw-flex tw-size-6 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-iron-600 tw-bg-iron-800 tw-text-iron-300 tw-transition tw-duration-300 hover:tw-border-primary-400 hover:tw-text-primary-300"><FontAwesomeIcon icon={faXmark} className="tw-size-3" /></button>;
      $[59] = handleClearEndDate;
      $[60] = hasSelectedEndDate;
      $[61] = t27;
    } else {
      t27 = $[61];
    }
    if ($[62] !== t26 || $[63] !== t27) {
      t9 = <div className="tw-mb-3 tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-start sm:tw-justify-between">{t23}<div className="tw-rounded-lg tw-bg-iron-700/40 tw-px-3 tw-py-2 tw-shadow-md">{t24}<div className="tw-flex tw-items-center tw-gap-x-2">{t26}{t27}</div></div></div>;
      $[62] = t26;
      $[63] = t27;
      $[64] = t9;
    } else {
      t9 = $[64];
    }
    if ($[65] !== hasRankFutureDateError) {
      t10 = hasRankFutureDateError && <div role="alert" className="tw-mb-3 tw-flex tw-items-center tw-gap-x-2 tw-rounded-lg tw-border tw-border-error/40 tw-bg-error/10 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-error"><FontAwesomeIcon icon={faTriangleExclamation} className="tw-size-4 tw-flex-shrink-0" aria-hidden="true" /><span>Wave end date must be in the future.</span></div>;
      $[65] = hasRankFutureDateError;
      $[66] = t10;
    } else {
      t10 = $[66];
    }
    if ($[67] !== hasEndDateBeforeVotingStartError) {
      t11 = hasEndDateBeforeVotingStartError && <div role="alert" className="tw-mb-3 tw-flex tw-items-center tw-gap-x-2 tw-rounded-lg tw-border tw-border-error/40 tw-bg-error/10 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-error"><FontAwesomeIcon icon={faTriangleExclamation} className="tw-size-4 tw-flex-shrink-0" aria-hidden="true" /><span>Wave end date cannot be before voting begins.</span></div>;
      $[67] = hasEndDateBeforeVotingStartError;
      $[68] = t11;
    } else {
      t11 = $[68];
    }
    t7 = "tw-grid tw-grid-cols-1 tw-gap-x-10 tw-gap-y-8 md:tw-grid-cols-2";
    t5 = "tw-w-full";
    if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <p className="tw-mb-2 tw-text-sm tw-font-medium tw-text-iron-300">Select Official End Date:</p>;
      $[69] = t6;
    } else {
      t6 = $[69];
    }
    T0 = CommonCalendar;
    t3 = displayedDate.getMonth();
    t4 = displayedDate.getFullYear();
    $[4] = dates;
    $[5] = hasEndDateBeforeVotingStartError;
    $[6] = hasRankFutureDateError;
    $[7] = hasSelectedEndDate;
    $[8] = selectedEndDate;
    $[9] = setDates;
    $[10] = setIsExpanded;
    $[11] = shouldShowExpandedContent;
    $[12] = T0;
    $[13] = T1;
    $[14] = calculateTotalDecisions;
    $[15] = displayedHours;
    $[16] = displayedMinutes;
    $[17] = handleDateSelection;
    $[18] = handleTimeChange;
    $[19] = minEndDate;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
    $[24] = t14;
    $[25] = t15;
    $[26] = t16;
    $[27] = t17;
    $[28] = t18;
    $[29] = t3;
    $[30] = t4;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
  } else {
    T0 = $[12];
    T1 = $[13];
    calculateTotalDecisions = $[14];
    displayedHours = $[15];
    displayedMinutes = $[16];
    handleDateSelection = $[17];
    handleTimeChange = $[18];
    minEndDate = $[19];
    t10 = $[20];
    t11 = $[21];
    t12 = $[22];
    t13 = $[23];
    t14 = $[24];
    t15 = $[25];
    t16 = $[26];
    t17 = $[27];
    t18 = $[28];
    t3 = $[29];
    t4 = $[30];
    t5 = $[31];
    t6 = $[32];
    t7 = $[33];
    t8 = $[34];
    t9 = $[35];
  }
  let t19;
  if ($[70] !== T0 || $[71] !== handleDateSelection || $[72] !== minEndDate || $[73] !== selectedEndDate || $[74] !== t3 || $[75] !== t4) {
    t19 = <T0 initialMonth={t3} initialYear={t4} selectedTimestamp={selectedEndDate} minTimestamp={minEndDate} maxTimestamp={null} setSelectedTimestamp={handleDateSelection} />;
    $[70] = T0;
    $[71] = handleDateSelection;
    $[72] = minEndDate;
    $[73] = selectedEndDate;
    $[74] = t3;
    $[75] = t4;
    $[76] = t19;
  } else {
    t19 = $[76];
  }
  let t20;
  if ($[77] !== t19 || $[78] !== t5 || $[79] !== t6) {
    t20 = <div className={t5}>{t6}{t19}</div>;
    $[77] = t19;
    $[78] = t5;
    $[79] = t6;
    $[80] = t20;
  } else {
    t20 = $[80];
  }
  let t21;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <p className="tw-mb-2 tw-text-sm tw-font-medium tw-text-iron-300">Select Time:</p>;
    $[81] = t21;
  } else {
    t21 = $[81];
  }
  const t22 = !hasSelectedEndDate;
  let t23;
  if ($[82] !== displayedHours || $[83] !== displayedMinutes || $[84] !== handleTimeChange || $[85] !== t22) {
    t23 = <TimePicker hours={displayedHours} minutes={displayedMinutes} onTimeChange={handleTimeChange} disabled={t22} />;
    $[82] = displayedHours;
    $[83] = displayedMinutes;
    $[84] = handleTimeChange;
    $[85] = t22;
    $[86] = t23;
  } else {
    t23 = $[86];
  }
  let t24;
  if ($[87] !== hasSelectedEndDate) {
    t24 = !hasSelectedEndDate && <p className="tw-mt-2 tw-text-xs tw-text-iron-400">Pick an end date first. Recurring announcements currently have no end date.</p>;
    $[87] = hasSelectedEndDate;
    $[88] = t24;
  } else {
    t24 = $[88];
  }
  let t25;
  if ($[89] !== dates.firstDecisionTime || $[90] !== dates.subsequentDecisions || $[91] !== isRollingMode || $[92] !== selectedEndDate) {
    t25 = selectedEndDate !== null && isRollingMode && <div className="tw-mt-4 tw-rounded-lg tw-bg-primary-500/10 tw-p-2"><p className="tw-mb-0 tw-text-xs"><span className="tw-text-iron-300">Last winner announcement will be at:</span></p><p className="tw-mb-0 tw-text-sm tw-font-medium tw-text-primary-400">{formatDate(calculateLastDecisionTime(dates.firstDecisionTime, dates.subsequentDecisions, selectedEndDate))}</p></div>;
    $[89] = dates.firstDecisionTime;
    $[90] = dates.subsequentDecisions;
    $[91] = isRollingMode;
    $[92] = selectedEndDate;
    $[93] = t25;
  } else {
    t25 = $[93];
  }
  let t26;
  if ($[94] !== t23 || $[95] !== t24 || $[96] !== t25) {
    t26 = <div className="tw-w-full">{t21}{t23}{t24}{t25}</div>;
    $[94] = t23;
    $[95] = t24;
    $[96] = t25;
    $[97] = t26;
  } else {
    t26 = $[97];
  }
  let t27;
  if ($[98] !== t20 || $[99] !== t26 || $[100] !== t7) {
    t27 = <div className={t7}>{t20}{t26}</div>;
    $[98] = t20;
    $[99] = t26;
    $[100] = t7;
    $[101] = t27;
  } else {
    t27 = $[101];
  }
  let t28;
  if ($[102] !== t10 || $[103] !== t11 || $[104] !== t27 || $[105] !== t8 || $[106] !== t9) {
    t28 = <div className={t8}>{t9}{t10}{t11}{t27}</div>;
    $[102] = t10;
    $[103] = t11;
    $[104] = t27;
    $[105] = t8;
    $[106] = t9;
    $[107] = t28;
  } else {
    t28 = $[107];
  }
  const t29 = isRollingMode ? "About Recurring Winners" : "About Wave End Date";
  let t30;
  if ($[108] !== t29) {
    t30 = <p className="tw-mb-1 tw-text-sm tw-font-medium tw-text-iron-200">{t29}</p>;
    $[108] = t29;
    $[109] = t30;
  } else {
    t30 = $[109];
  }
  let t31;
  if ($[110] !== isRollingMode) {
    t31 = isRollingMode ? <><p className="tw-mb-2 tw-text-xs tw-text-iron-400">In recurring mode, your wave continues announcing winners in regular intervals until an optional end date.</p><p className="tw-mb-2 tw-text-xs tw-text-iron-400">Leave the end date blank to keep recurring announcements open-ended.</p></> : <p className="tw-mb-2 tw-text-xs tw-text-iron-400">Your wave will end immediately after the final winner announcement.</p>;
    $[110] = isRollingMode;
    $[111] = t31;
  } else {
    t31 = $[111];
  }
  let t32;
  if ($[112] !== calculateTotalDecisions || $[113] !== isRollingMode || $[114] !== selectedEndDate) {
    t32 = selectedEndDate !== null && isRollingMode && <div className="tw-mt-3 tw-rounded-lg tw-bg-primary-500/10 tw-p-2"><p className="tw-mb-0 tw-flex tw-items-center tw-justify-between tw-text-xs"><span className="tw-text-iron-200">Total winner announcements:</span><span className="tw-font-semibold tw-text-primary-400">{calculateTotalDecisions()}</span></p></div>;
    $[112] = calculateTotalDecisions;
    $[113] = isRollingMode;
    $[114] = selectedEndDate;
    $[115] = t32;
  } else {
    t32 = $[115];
  }
  let t33;
  if ($[116] !== t30 || $[117] !== t31 || $[118] !== t32) {
    t33 = <div className="tw-mt-4 tw-rounded-lg tw-bg-iron-800/30 tw-p-3">{t30}{t31}{t32}</div>;
    $[116] = t30;
    $[117] = t31;
    $[118] = t32;
    $[119] = t33;
  } else {
    t33 = $[119];
  }
  let t34;
  if ($[120] !== t12 || $[121] !== t28 || $[122] !== t33) {
    t34 = <div className={t12}>{t28}{t33}</div>;
    $[120] = t12;
    $[121] = t28;
    $[122] = t33;
    $[123] = t34;
  } else {
    t34 = $[123];
  }
  let t35;
  if ($[124] !== T1 || $[125] !== t13 || $[126] !== t14 || $[127] !== t15 || $[128] !== t16 || $[129] !== t17 || $[130] !== t34) {
    t35 = <T1 title={t13} titleActions={t14} isExpanded={t15} onToggle={t16} collapsedContent={t17}>{t34}</T1>;
    $[124] = T1;
    $[125] = t13;
    $[126] = t14;
    $[127] = t15;
    $[128] = t16;
    $[129] = t17;
    $[130] = t34;
    $[131] = t35;
  } else {
    t35 = $[131];
  }
  let t36;
  if ($[132] !== t18 || $[133] !== t35) {
    t36 = <div className={t18}>{t35}</div>;
    $[132] = t18;
    $[133] = t35;
    $[134] = t36;
  } else {
    t36 = $[134];
  }
  return t36;
}